import * as React from "react";
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Table, Tag, notification } from "antd";
import moment from "moment";
import { centered_flex_box, MainInput, main_button, left_flex_box } from "../../app/components/Styles";
import { BrandSelect } from "../../app/components/BrandSelect";
import { antTableWrapperSx, antTableCellWrapSx, idTableColumn } from "../../app/components/AntTableStyles";
import ImageComponent from "../../app/components/ImageComponent";
import { getInbounds, getInboundItems, getVendors, groupInboundPrRef, downloadInboundChecklist, downloadInboundReceipt } from "../../app/store/actions/imsAdminActions";
import { useEffect } from "react";

const statusColor = (status) => {
  if (status === "Received") return "green";
  if (status === "Cancelled") return "red";
  if (status === "Pending") return "gold";
  return "blue";
};

const canDownloadReceipt = (status) => status === "Received" || status === "Partially Received";

export const InboundsByBrand = ({
  getInbounds,
  getInboundItems,
  getVendors,
  groupInboundPrRef,
  downloadInboundChecklist,
  downloadInboundReceipt,
  inbounds,
  vendors,
  isLoading,
}) => {
  const navigate = useNavigate();
  const [vendorId, setVendorId] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [selected, setSelected] = React.useState([]);
  const [prRef, setPrRef] = React.useState("");
  const [groupOpen, setGroupOpen] = React.useState(false);
  const [itemsByInbound, setItemsByInbound] = React.useState({});
  const [loadingItems, setLoadingItems] = React.useState({});

  const loadInbounds = (targetPage = page) => {
    getInbounds({
      page: targetPage,
      limit: 100,
      direction: "In",
      mainWarehouse: true,
      ...(vendorId ? { vendorId } : {}),
    });
  };

  useEffect(() => {
    getVendors({ page: 1, limit: 1000 });
  }, []);

  useEffect(() => {
    loadInbounds(page);
    setSelected([]);
  }, [page, vendorId]);

  const docs = inbounds?.docs || [];
  const selectedRows = docs.filter((row) => selected.includes(row._id));
  const selectedVendors = new Set(selectedRows.map((row) => row.vendor?._id));
  const selectedPrRefs = new Set(selectedRows.map((row) => row.prRef || ""));
  const sameBrand = selectedVendors.size === 1;
  const sharedPrRef = selectedPrRefs.size === 1 ? [...selectedPrRefs][0] : "";

  const groups = React.useMemo(() => {
    const byBrand = new Map();
    docs.forEach((row) => {
      const key = row.vendor?._id || "unknown";
      if (!byBrand.has(key)) {
        byBrand.set(key, {
          key,
          name: row.vendor?.brandName || row.vendor?.name || "Unknown brand",
          rows: [],
        });
      }
      byBrand.get(key).rows.push(row);
    });
    return [...byBrand.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [docs]);

  const columns = [
    idTableColumn({ title: "Inbound ID" }),
    { title: "Warehouse", dataIndex: "warehouse" },
    { title: "Type", dataIndex: "type", render: (type) => <Tag color={type === "Returns" ? "orange" : "blue"}>{type}</Tag> },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => <Tag color={statusColor(status)}>{(status || "").toUpperCase()}</Tag>,
    },
    { title: "Purchase Request", dataIndex: "prRef", render: (value) => value || "—" },
    { title: "Created", dataIndex: "createdAt" },
    {
      title: "Receipt",
      render: (_, record) => {
        const enabled = canDownloadReceipt(record.status);
        return (
          <Button
            size="small"
            disabled={!enabled}
            onClick={(event) => {
              event.stopPropagation();
              if (!enabled) return;
              downloadInboundReceipt(record.prRef ? { prRef: record.prRef } : { inboundId: record._id });
            }}
          >
            PDF
          </Button>
        );
      },
    },
  ];

  const loadItems = (inboundId) => {
    if (itemsByInbound[inboundId] || loadingItems[inboundId]) return;
    setLoadingItems((current) => ({ ...current, [inboundId]: true }));
    getInboundItems(inboundId)
      .then((items) => {
        setItemsByInbound((current) => ({ ...current, [inboundId]: items || [] }));
      })
      .catch(() => {})
      .finally(() => {
        setLoadingItems((current) => ({ ...current, [inboundId]: false }));
      });
  };

  const expandedRowRender = (record) => {
    const items = itemsByInbound[record._id];
    const columns = [
      { title: "SKU", dataIndex: "sku" },
      {
        title: "Image",
        dataIndex: "image",
        render: (src) => <ImageComponent width={50} height={50} src={src} preview={true} />,
      },
      { title: "Name", dataIndex: "name" },
      { title: "Requested", dataIndex: "quantity" },
      { title: "Received", dataIndex: "quantityReceived" },
    ];
    const data = (items || []).map((item) => ({
      _id: item._id,
      sku: item.product?.sku || item.product?.externalId || "—",
      image: item.product?.image,
      name: item.product?.name || "—",
      quantity: item.quantity,
      quantityReceived: item.quantityReceived ?? 0,
    }));
    return (
      <Box sx={antTableCellWrapSx}>
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={data}
          loading={Boolean(loadingItems[record._id]) && !items}
          pagination={false}
        />
      </Box>
    );
  };

  const openGroup = () => {
    if (!sameBrand) {
      notification.error({ message: "Select inbounds from one brand" });
      return;
    }
    setPrRef("");
    setGroupOpen(true);
  };

  const handleGroup = () => {
    if (!prRef.trim()) {
      notification.error({ message: "Enter a Purchase Request number" });
      return;
    }
    groupInboundPrRef({ inboundIds: selected, prRef: prRef.trim() }, (ok) => {
      if (ok) {
        setGroupOpen(false);
        setPrRef("");
        setSelected([]);
        loadInbounds(page);
      }
    });
  };

  return (
    <Box sx={{ width: "100%", padding: 5, mt: 2 }}>
      <Box sx={{ ...centered_flex_box, mb: 2, gap: 2 }}>
        <Typography variant="h4" sx={{ color: "var(--secColor)" }}>Inbounds by brand</Typography>
        <Button sx={main_button} onClick={() => navigate("/ims/admin/inbounds")}>SKU list</Button>
        <Button sx={main_button} onClick={() => navigate("/ims/admin/item-updates")}>Item updates</Button>
      </Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ ...left_flex_box, flexWrap: "wrap", gap: 1 }}>
            <BrandSelect
              vendors={vendors}
              value={vendorId}
              label="Brand"
              emptyLabel="All brands"
              emptyValue=""
              sx={{ minWidth: "20vw", marginInlineEnd: 2 }}
              onChange={(id) => { setVendorId(id); setPage(1); }}
            />
            <Button sx={main_button} disabled={selected.length < 2 || isLoading} onClick={openGroup}>
              Group Inbounds
            </Button>
            <Button
              sx={main_button}
              disabled={!sharedPrRef || isLoading}
              onClick={() => downloadInboundChecklist(sharedPrRef)}
            >
              Checklist PDF
            </Button>
          </Box>
          <Typography sx={{ mt: 1 }} color="text.secondary">
            {selected.length
              ? `${selected.length} selected${sameBrand ? "" : " — pick one brand"}`
              : "Main warehouse only. Select at least two inbounds from one brand, then Group Inbounds."}
          </Typography>
        </CardContent>
      </Card>

      {groups.map((group) => (
        <Box key={group.key} sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: "var(--secColor)", mb: 1 }}>{group.name}</Typography>
          <Box sx={antTableWrapperSx(false, 900)}>
            <Table
              rowKey="_id"
              columns={columns}
              loading={isLoading}
              dataSource={group.rows.map((row) => ({
                ...row,
                warehouse: row.targetWarehouse?.name || "N/A",
                type: row.isRTO ? "Returns" : "Main",
                createdAt: row.createdAt ? moment(row.createdAt).format("lll") : "",
              }))}
              rowSelection={{
                selectedRowKeys: selected.filter((id) => group.rows.some((row) => row._id === id)),
                onChange: (keys) => {
                  const other = selected.filter((id) => !group.rows.some((row) => row._id === id));
                  setSelected([...other, ...keys]);
                },
                getCheckboxProps: (record) => ({
                  disabled: record.status === "Cancelled" || record.direction === "Out",
                }),
              }}
              expandable={{
                expandedRowRender,
                onExpand: (expanded, record) => {
                  if (expanded) loadItems(record._id);
                },
              }}
              pagination={false}
            />
          </Box>
        </Box>
      ))}

      <Dialog open={groupOpen} onClose={() => setGroupOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Group Inbounds</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {selected.length} inbound{selected.length === 1 ? "" : "s"} from one brand will share this Purchase Request number.
          </Typography>
          <MainInput
            autoFocus
            fullWidth
            label="Purchase Request"
            value={prRef}
            onChange={({ target }) => setPrRef(target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleGroup();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupOpen(false)}>Cancel</Button>
          <Button sx={main_button} disabled={isLoading || !prRef.trim()} onClick={handleGroup}>Set Purchase Request</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 1 }}>
        <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
        <Button disabled={page >= (inbounds?.totalPages || 1)} onClick={() => setPage(page + 1)} sx={{ ml: 1 }}>Next</Button>
        <Typography component="span" sx={{ ml: 2 }}>
          Page {page} of {inbounds?.totalPages || 1}
        </Typography>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  isLoading: state?.records?.isLoading || state?.wait?.isLoading,
  inbounds: state?.records?.inbounds,
  vendors: state?.records?.vendors,
});

export default connect(mapStateToProps, {
  getInbounds,
  getInboundItems,
  getVendors,
  groupInboundPrRef,
  downloadInboundChecklist,
  downloadInboundReceipt,
})(InboundsByBrand);
